import './Record.scss';

import React, { useContext, useState } from 'react';
import { Record as RecordSVG } from '@bblabs/module-library/svgs';
import { LoadingDots } from '@bblabs/module-library/atoms';
import { UserConfigContext } from '@bblabs/module-library/classes';
import { RecorderSVG } from '../SVGS/RecorderSVG';
import { ScreenRecorderSVG } from '../SVGS/ScreenRecorderSVG';
import { getAuthenticatedApiService } from '../services/AuthenticatedApi';
import { userService } from '../services/UserService';
import { LOG_TYPE, MetricService } from '../services/MetricService';
import { isViewInReadMode } from '../abstractions/office';
import { environment, getUrlsForEnvironment } from '../../environment';
import { EnvironmentContext, UserContext } from './App';

export enum RECORDER_TYPE {
  RECORDER = 'recorder',
  SCREEN_RECORDER = 'screen_recorder',
}

export const Record = () => {
  const env = useContext(EnvironmentContext);
  const user = useContext(UserContext);
  const userConfig = useContext(UserConfigContext);
  const urls = getUrlsForEnvironment(environment);
  const [inRecording, setInRecording] = useState(false);

  const openRecorder = async (recorderType: RECORDER_TYPE) => {
    MetricService.increment(`click.task_pane.recorder_type.${recorderType}.button`);
    setInRecording(true);

    const api = getAuthenticatedApiService(env, userService);
    const url = new URL('/v2/onetimetoken', urls.apiUrl);

    // figure out the host domain for the outlook instance. default to
    // metatag domain and fallback to window.location.hostname
    const allowedHosts = [/^([/]+)?outlook-365.(dev.)?bombbomb.io$/, /^localhost(:\d+)?$/];
    const host = document.querySelector('meta[name="domain"]') as HTMLMetaElement;
    let domain = host?.content
      ? host.content.replace('https://', '').replace('/', '')
      : window.location.hostname;

    let allowed = false;
    for (let index = 0; index < allowedHosts.length; index++) {
      const allowedTest = allowedHosts[index];
      if (allowedTest.test(domain)) {
        allowed = true;
        continue;
      }
    }

    if (!allowed) {
      domain = window.location.hostname;
    }

    const destination = new URL('/app', urls.appUrl);
    destination.searchParams.append('module', 'videos');
    destination.searchParams.append('page', 'outlookRecorder');
    destination.searchParams.append('popup', '1');
    destination.searchParams.append('domain', domain);
    if (recorderType === 'screen_recorder') {
      destination.searchParams.append('launchScreenRecorder', '1');
    }

    const payload = {
      source: 'outlook365',
      destination: destination.toString(),
    };

    const response = await api.post(url, JSON.stringify(payload));
    const result = await response?.json();
    if (result.token) {
      const recorderWindow = new URL('/login/ott', urls.appUrl);
      recorderWindow.searchParams.append('ott', result.token);
      window.open(recorderWindow.toString());
      MetricService.yardstick('recorder.open');
      MetricService.log('Recorder window open via OTT', LOG_TYPE.INFO, {
        clientId: user.user?.get('clientId'),
      });
    } else {
      setInRecording(false);
      MetricService.yardstick('recorder.no_ott');
    }
  };

  function onButtonKeyDown(event: React.KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      event.target.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    }
  }

  function cancelRecording() {
    setInRecording(false);
    MetricService.yardstick('recorder.cancel');
  }

  function onCancelClick(event: React.MouseEvent) {
    event.preventDefault();
    cancelRecording();
  }

  function onCancelKeyDown(event: React.KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      cancelRecording();
    }
  }

  if (userConfig.state === 'loading') {
    return <LoadingDots />;
  }
  const showScreenRecorder = userConfig.config.SCREEN_RECORDER_ENABLED;
  return (
    <div>
      {isViewInReadMode() && !inRecording && (
        <div className="pane read-view">
          <div className="icon-container icon-container--record">
            <RecordSVG title="Recorder" fill="#f00000" />
          </div>
          <h2>Ready to Record?</h2>
          <p>Compose a new email to record</p>
        </div>
      )}
      {!isViewInReadMode() && !inRecording && (
        <div className="pane compose-view">
          <h3>{showScreenRecorder ? 'How do you want to record?' : 'Record a new video.'}</h3>
          <div
            className="icon-container"
            role="button"
            tabIndex={0}
            onKeyDown={onButtonKeyDown}
            onClick={() => openRecorder(RECORDER_TYPE.RECORDER)}
          >
            <RecorderSVG width="42" height="50.2" title="Camera" fill="#7A7979" secondaryFill="#f00000" />
            <h4>Record Yourself</h4>
          </div>
          {showScreenRecorder && (
            <div
              className="icon-container"
              role="button"
              tabIndex={0}
              onKeyDown={onButtonKeyDown}
              onClick={() => openRecorder(RECORDER_TYPE.SCREEN_RECORDER)}
            >
              <ScreenRecorderSVG
                title="Screen Recorder"
                width="59.19"
                height="45.7"
                fill="#7A7979"
                secondaryFill="#f00000"
              />
              <h4>Record Your Screen</h4>
            </div>
          )}
        </div>
      )}
      {inRecording && (
        <div className="pane in-recording">
          <div className="icon-container icon-container--record">
            <RecordSVG title="Recorder" fill="#f00000" />
          </div>
          <h2>Recording in Progress</h2>
          <p>Can&#39;t find our recorder? Check your web browser.</p>
          <p>
            <a
              href="#"
              role="button"
              onKeyDown={onCancelKeyDown}
              onClick={onCancelClick}
              className="button--cancel"
            >
              Cancel
            </a>
          </p>
        </div>
      )}
    </div>
  );
};
